import React, { useState } from 'react'
import styled from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'
import AttachFileIcon from '@mui/icons-material/AttachFile'

import useZendeskUpload from 'hooks/useZendeskUpload'
import { color } from 'styles/colors'

interface UploadedFile {
  file: File;
  token: string;
  sizeLabel: string;
}

interface Props {
  onUploadedChange: (tokens: string[]) => void;
}

export default function AttachmentUploader ({ onUploadedChange }: Props) {
  const { uploadScreenshot, loading } = useZendeskUpload()
  const [error, serError] = useState<string | null>(null)
  const [files, setFiles] = useState<UploadedFile[]>([])

  const handleFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    serError(null)
    const selected = Array.from(e.target.files || [])
    if (!selected.length) return

    if (files.length + selected.length > 5) {
      serError('Select upto 5 photos')
      return
    }
    const validImages = selected.filter(file =>
      file.type.startsWith('image/')
    )
    if (validImages.length !== selected.length) {
      serError('Only photos are allowed')
      return
    }

    for (const file of selected) {
      const token = await uploadScreenshot(file)

      if (!token) continue

      const sizeLabel = (file.size / 1024).toFixed(0) + ' KB'

      const newFile: UploadedFile = { file, token, sizeLabel }

      setFiles((prev) => {
        const updated = [...prev, newFile]
        onUploadedChange(updated.map((f) => f.token))
        return updated
      })
    }
  }

  const removeAttachment = (token: string) => {
    const updated = files.filter((f) => f.token !== token)
    setFiles(updated)
    onUploadedChange(updated.map((f) => f.token))
  }

  return (
    <S.Wrapper>
      <S.Label>Photos {files.length > 0 && `(${files.length})`}</S.Label>

      {files.map((f) => (
        <S.FileRow key={f.token}>
          <S.FileInfo>
            <strong>{f.file.name}</strong>
            <span>{f.sizeLabel}</span>
          </S.FileInfo>

          <S.RemoveBtn onClick={() => removeAttachment(f.token)}>
            <CloseIcon fontSize="small" />
          </S.RemoveBtn>
        </S.FileRow>
      ))}

      {files.length < 5 && (
        <S.DropZone>
          <input
            type="file"
            multiple
            disabled={loading}
            onChange={handleFilesSelected}
            accept="image/*"
          />
          <S.PlaceholderText>
            <AttachFileIcon fontSize="small" />
            Add up to 5 photos
          </S.PlaceholderText>
        </S.DropZone>
      )}
      {error && <S.ErrorText>{error}</S.ErrorText>}
    </S.Wrapper>
  )
}

const S = {
  Wrapper: styled.div`
    margin-bottom: 1rem;
  `,
  Label: styled.div`
    font-weight: 600;
    font-size: 15px;
    margin-bottom: 8px;
  `,
  FileRow: styled.div`
    display: flex;
    align-items: center;
    border: 1px solid ${color('navbarArrowBackground')};
    padding: 8px 10px;
    border-radius: 8px;
    margin-bottom: 6px;
  `,
  FileInfo: styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    font-size: 13px;
  `,
  RemoveBtn: styled.button`
    border: none;
    background: transparent;
    font-size: 20px;
    cursor: pointer;
    line-height: 1;
    padding: 0 6px;
    color: ${color('navbarArrowBackground')};
    &:hover {
      color: ${color('warrantyText')};
    }
  `,
  DropZone: styled.label`
    border: 2px dashed ${color('disabledText')};
    border-radius: 0.5rem;
    padding: 1rem;
    display: block;
    text-align: center;
    cursor: pointer;
    position: relative;
    margin-top: 10px;
    input {
      opacity: 0;
      width: 100%;
      height: 100%;
      position: absolute;
      cursor: pointer;
      top: 0;
      left: 0;
    }
  `,
  PlaceholderText: styled.div`
    font-size: 1.025rem;
    align-items: center;
    display: flex;
    justify-content: center;
    gap: 5px;
    svg{
      color: ${color('secondaryButtonBorder')};
    }
  `,
  ErrorText: styled.p`
    color: ${color('errorText')};
    font-size: 0.875rem;
    margin: 4px 0 0 0;
    position: relative;
    top: -15px;
  `
}
